const readAscii = (view, offset, length) => Array.from({ length }, (_, index) => String.fromCharCode(view.getUint8(offset + index))).join("");

const readVariableLength = (view, state, end) => {
  let value = 0;
  let count = 0;
  while (state.offset < end && count < 4) {
    const byte = view.getUint8(state.offset++);
    value = (value << 7) | (byte & 0x7f);
    count += 1;
    if (!(byte & 0x80)) return value;
  }
  throw new Error("Data MIDI variable-length tidak valid");
};

export const parseMidiFile = (arrayBuffer, filename = "MIDI Sequence") => {
  const view = new DataView(arrayBuffer);
  if (view.byteLength < 14 || readAscii(view, 0, 4) !== "MThd") throw new Error("File bukan Standard MIDI File");
  const headerLength = view.getUint32(4);
  const format = view.getUint16(8);
  const trackCount = view.getUint16(10);
  const division = view.getUint16(12);
  if (headerLength < 6 || format > 1 || !trackCount || division & 0x8000) throw new Error("Format MIDI belum didukung");
  const ppq = division;
  const events = [];
  const tempos = [{ tick: 0, bpm: 120 }];
  let offset = 8 + headerLength;
  let durationTicks = 0;
  for (let track = 0; track < trackCount; track += 1) {
    if (offset + 8 > view.byteLength || readAscii(view, offset, 4) !== "MTrk") throw new Error("Track MIDI rusak atau terpotong");
    const length = view.getUint32(offset + 4);
    const end = offset + 8 + length;
    if (end > view.byteLength) throw new Error("Ukuran track MIDI tidak valid");
    const state = { offset: offset + 8 };
    let tick = 0;
    let runningStatus = 0;
    while (state.offset < end) {
      tick += readVariableLength(view, state, end);
      durationTicks = Math.max(durationTicks, tick);
      let status = view.getUint8(state.offset);
      if (status & 0x80) {
        state.offset += 1;
        if (status < 0xf0) runningStatus = status;
      } else if (runningStatus) status = runningStatus;
      else throw new Error("Running status MIDI tidak valid");
      if (status === 0xff) {
        const type = view.getUint8(state.offset++);
        const metaLength = readVariableLength(view, state, end);
        if (type === 0x51 && metaLength === 3) {
          const micros = (view.getUint8(state.offset) << 16) | (view.getUint8(state.offset + 1) << 8) | view.getUint8(state.offset + 2);
          if (micros > 0) tempos.push({ tick, bpm: Number((60000000 / micros).toFixed(4)) });
        }
        state.offset += metaLength;
        runningStatus = 0;
        continue;
      }
      if (status === 0xf0 || status === 0xf7) {
        state.offset += readVariableLength(view, state, end);
        runningStatus = 0;
        continue;
      }
      const command = status & 0xf0;
      const channel = status & 0x0f;
      const first = view.getUint8(state.offset++);
      const oneByte = command === 0xc0 || command === 0xd0;
      const second = oneByte ? 0 : view.getUint8(state.offset++);
      if (command === 0x90 && second > 0) events.push({ tick, type: "note_on", note: first, velocity: second, channel });
      else if (command === 0x80 || (command === 0x90 && second === 0)) events.push({ tick, type: "note_off", note: first, velocity: second, channel });
      else if (command === 0xb0 && first === 64) events.push({ tick, type: "control_change", controller: first, value: second, channel });
    }
    offset = end;
  }
  events.sort((left, right) => left.tick - right.tick);
  if (!events.some((event) => event.type === "note_on")) throw new Error("File MIDI tidak memiliki event note");
  if (events.length > 8192) throw new Error("File MIDI terlalu kompleks (maksimal 8192 event note)");
  const uniqueTempos = [...new Map(tempos.sort((a, b) => a.tick - b.tick).map((tempo) => [tempo.tick, tempo])).values()];
  return {
    id: globalThis.crypto?.randomUUID?.() || `midi-${Date.now()}`,
    name: String(filename || "MIDI Sequence").replace(/\.(mid|midi)$/i, "").slice(0, 160),
    ppq,
    durationTicks,
    events,
    tempos: uniqueTempos,
  };
};

export const midiTickToSeconds = (sequence, targetTick) => {
  const tempos = sequence?.tempos?.length ? sequence.tempos : [{ tick: 0, bpm: 120 }];
  const ppq = Math.max(1, Number(sequence?.ppq) || 480);
  let seconds = 0;
  let previousTick = 0;
  let bpm = 120;
  for (const tempo of tempos) {
    if (tempo.tick > targetTick) break;
    seconds += ((tempo.tick - previousTick) / ppq) * (60 / bpm);
    previousTick = tempo.tick;
    bpm = tempo.bpm;
  }
  return seconds + ((targetTick - previousTick) / ppq) * (60 / bpm);
};
